// Shared browser/Worker contract. Both tsconfig.app.json and tsconfig.worker.json include `shared/`.

/** Which practice scenario the room runs. */
export const SCENARIO_IDS = [
  // Default. Cassie is the TA Specialist running a 40-minute screening call; the AI plays the
  // Business Development Associate (BDA) candidate (as Ideals' TA Lead would in the real role play).
  'roleplay_screening',
  // The final 15 minutes: Tania (TA Manager) debriefs the role play and asks hard TA/GTM questions.
  'tania_followup',
  // Cassie is the candidate in a competency-based interview for Senior TA Specialist (APAC).
  'ta_competency',
  // Role play of a hiring-manager intake call (AI = Ideals sales leader opening a GTM role in APAC).
  'hiring_manager_intake',
  // Generic interview from the user's own role/focus/materials (legacy behaviour).
  'custom',
] as const;
export type ScenarioId = (typeof SCENARIO_IDS)[number];

/** Interviewer / role-play counterpart style (user request: harsh, gentle, supportive).
 *  Applies to every scenario: in roleplay_screening it shapes how the candidate (played by Tania)
 *  reacts to weak questioning; in interviewer scenarios it shapes Tania's / the interviewer's tone. */
export const PERSONA_IDS = ['harsh', 'gentle', 'supportive'] as const;
export type PersonaId = (typeof PERSONA_IDS)[number];

/** How the AI candidate behaves in `roleplay_screening`. Ignored by other scenarios. */
export const CANDIDATE_BEHAVIORS = ['cooperative', 'surface_level', 'evasive', 'challenging'] as const;
export type CandidateBehavior = (typeof CANDIDATE_BEHAVIORS)[number];

/** Built-in AI candidate profiles for `roleplay_screening`; 'from_materials' = use an uploaded CV. */
export const CANDIDATE_PROFILE_IDS = ['official_tania_min', 'solid_sdr', 'inflated_numbers', 'career_switcher', 'from_materials'] as const;
export type CandidateProfileId = (typeof CANDIDATE_PROFILE_IDS)[number];

export const DIFFICULTIES = ['warm_up', 'standard', 'stretch'] as const;
export type Difficulty = (typeof DIFFICULTIES)[number];

export const INTERVIEW_LANGUAGES = ['en', 'zh'] as const;
export type InterviewLanguage = (typeof INTERVIEW_LANGUAGES)[number];

/** Female voices only — Tania (and every AI counterpart) is a woman. */
export const VOICES = ['marin', 'coral', 'sage', 'shimmer'] as const;
export type VoiceId = (typeof VOICES)[number];

/** How Tania's English sounds. She is Ukrainian (native Ukrainian/Russian, Kyiv-educated) and has lived in Madrid since ~2022. */
export const ACCENT_IDS = ['ukrainian', 'spanish', 'neutral'] as const;
export type AccentId = (typeof ACCENT_IDS)[number];

export const DURATIONS = [10, 20, 40, 60] as const;
export type DurationMinutes = (typeof DURATIONS)[number];

export const MATERIAL_KINDS = ['booklet', 'job_brief', 'candidate_cv', 'cbi_guide', 'company', 'my_cv', 'other'] as const;
export type MaterialKind = (typeof MATERIAL_KINDS)[number];

export interface MaterialDoc {
  id: string;
  kind: MaterialKind;
  name: string;
  /** Plain text / light markdown. Max 20 000 chars each, 60 000 total per request. */
  text: string;
}

export const MAX_MATERIAL_CHARS = 20_000;
export const MAX_MATERIALS_TOTAL_CHARS = 60_000;
export const MAX_MATERIALS = 8;

export interface InterviewSetup {
  scenario: ScenarioId;
  persona: PersonaId;
  candidateBehavior: CandidateBehavior;
  candidateProfile: CandidateProfileId;
  difficulty: Difficulty;
  durationMinutes: DurationMinutes;
  /** Free-text extra focus / instructions from the user (≤ 600 chars). */
  focus: string;
  language: InterviewLanguage;
  /** Target role label, e.g. "Senior Talent Acquisition Specialist (APAC)". ≤ 80 chars. */
  role: string;
  voice: VoiceId;
  voiceEngine: VoiceEngine;
  /** Default 'ukrainian'. */
  accent: AccentId;
  /** Include the built-in Ideals pack (booklet, job brief, company brief, CBI guide). Default true. */
  useDefaultMaterials: boolean;
  /** User uploads (already extracted to text by POST /api/materials/extract). */
  materials: MaterialDoc[];
}

/** POST /api/realtime/session  (body: InterviewSetup, ≤ 256 KB) */
export interface RealtimeCredential {
  expiresAt?: number;
  instructions: string;
  /** First hidden [SYSTEM] message the browser sends after connecting. */
  kickoff: string;
  model: string;
  sessionId?: string;
  value: string;
  voice: VoiceId;
}

/** POST /api/materials/extract  multipart/form-data field `file` (pdf, docx, txt, md, png, jpg; ≤ 8 MB)
 *  → MaterialExtractResponse. Worker uses gpt-6.1-sol (Responses API, input_file / input_image) for
 *  non-text files and returns faithful plain text (truncated to MAX_MATERIAL_CHARS). */
export interface MaterialExtractResponse {
  name: string;
  text: string;
  truncated: boolean;
}

export type TranscriptPhase = 'opening' | 'screening' | 'qa';
export interface TranscriptLine {
  role: 'user' | 'ai';
  text: string;
  /** roleplay_screening only: which phase the line belongs to (browser-tracked). */
  phase?: TranscriptPhase;
}

/** POST /api/debrief  body DebriefRequest (≤ 256 KB) → DebriefResponse.
 *  gpt-6.1-sol writes a scenario-aware markdown debrief. For roleplay_screening it includes:
 *  scores vs. Ideals' "What we will be looking at" list, missed probes, the user's CBI/CARE question
 *  quality, and a ready-to-send ≤1-page follow-up summary to Tania & Marina (verdict, rationale,
 *  Planning & Organization indicator scores 1–4 + overall). */
export interface DebriefRequest {
  setup: InterviewSetup;
  transcript: TranscriptLine[];
  notes: string;
}
export interface DebriefResponse {
  markdown: string;
  model: string;
}

/** Latest/highest-intelligence OpenAI text model (docs 2026-10: "GPT-6 Astra — highest intelligence"). */
export const TEXT_MODEL = 'gpt-6.1-sol';
/** Model for debriefs and progress reports (via the Agent Router Hub relay). */
export const DEBRIEF_MODEL = 'gpt-6-astra';
/** Voice engines. gpt-live-1 (GPT-Live, /v1/live/sessions) is newest; gpt-realtime-2.1 is the proven fallback. */
export const VOICE_ENGINES = ['gpt-live-1', 'gpt-realtime-2.1'] as const;
export type VoiceEngine = (typeof VOICE_ENGINES)[number];
export const REALTIME_MODEL = 'gpt-realtime-2.1';
export const LIVE_MODEL = 'gpt-live-1';
/** POST /api/live/connect  body { setup, sdp } -> LiveConnectResponse (browser then sets `sdp` as the WebRTC answer). */
export interface LiveConnectResponse {
  sdp: string;
  sessionId?: string;
  kickoff: string;
  voice: VoiceId;
  model: string;
}

/** roleplay_screening runs as three phases: Tania opens (clarify), the AI plays the BDA candidate, Tania runs Q&A. */
export function roleplayPhaseMinutes(duration: DurationMinutes): { opening: number; screening: number; qa: number } {
  switch (duration) {
    case 10:
      return { opening: 1, screening: 7, qa: 2 };
    case 20:
      return { opening: 2, screening: 14, qa: 4 };
    case 40:
      return { opening: 3, screening: 30, qa: 7 };
    case 60:
      return { opening: 5, screening: 40, qa: 15 };
  }
}
/** Hidden messages the browser sends via sendMessage to switch roles (also valid when sent by the model's own cue). */
export const ROLEPLAY_START_MESSAGE =
  '[SYSTEM] START_ROLEPLAY: The opening phase is over. Say you are switching into the candidate now and invite Cassie to start whenever she is ready, then speak only as the candidate.';
export const ROLEPLAY_END_MESSAGE =
  '[SYSTEM] END_ROLEPLAY: The screening call time is up. If the call has not been closed, let the candidate wrap up in one natural sentence, then step out of character as Tania and start the Q&A phase.';

/** POST /api/progress  body ProgressRequest (<= 20 sessions, <= 256 KB) -> ProgressResponse.
 *  Cross-session coaching report from past debriefs (gpt-6.1-sol). */
export interface ProgressSession {
  date: string;
  scenario: ScenarioId;
  persona: PersonaId;
  debriefMarkdown: string;
}
export interface ProgressRequest {
  sessions: ProgressSession[];
}
export interface ProgressResponse {
  markdown: string;
  model: string;
}

/* ------------------------------------------------------------------ */
/* Server-side session records + async feedback (D1)                    */
/* All endpoints need the login cookie, return no-store JSON, errors as { error: { code, message } }. */
/* ------------------------------------------------------------------ */

export type SessionStatus = 'live' | 'debriefing' | 'done' | 'failed';

export interface SessionSummary {
  id: string;
  createdAt: number; // epoch ms
  endedAt: number | null;
  updatedAt: number;
  scenario: ScenarioId;
  persona: PersonaId;
  candidateProfile: CandidateProfileId;
  durationMinutes: DurationMinutes;
  voiceEngine: VoiceEngine;
  status: SessionStatus;
  /** First line of the "Top 3 improvements" section once status === 'done'. */
  topImprovement: string | null;
}

export interface SessionRecord extends SessionSummary {
  setup: InterviewSetup;
  transcript: TranscriptLine[];
  notes: string;
  debriefMarkdown: string | null;
  /** 'none (short-session guard)' when the conversation was too thin for a model report. */
  debriefModel: string | null;
  debriefEffort: string | null;
  /** Bumped whenever the debrief prompt/structure changes; 0 for sessions without a report. */
  summaryVersion: number;
  /** When report generation started / finished (epoch ms), for debugging. */
  jobStartedAt: number | null;
  jobFinishedAt: number | null;
  error: string | null;
}

/** POST /api/sessions { setup } -> { id }  (call when a room starts; status 'live'). While a room is open keep calling PUT .../transcript at least every 60 s: a live session with no update for 10 min is finished automatically. */
export interface CreateSessionResponse {
  id: string;
}
/** PUT /api/sessions/:id/transcript  body SessionTranscriptBody (<= 512 KB) -> { ok: true }  (autosave; 409 if not 'live') */
export interface SessionTranscriptBody {
  transcript: TranscriptLine[];
  notes: string;
}
/** POST /api/sessions/:id/finish  body SessionTranscriptBody -> SessionRecord (status 'debriefing'; a very short conversation completes instantly as 'done' with a short "not enough conversation" summary and no model call).
 *  The debrief is generated in the background (minutes). Poll GET /api/sessions/:id every ~5-10 s until status is 'done' | 'failed'. */
/** GET /api/sessions -> SessionListResponse (newest first) */
export interface SessionListResponse {
  sessions: SessionSummary[];
}
/** GET /api/sessions/:id -> SessionRecord (polling an in-flight debrief advances it).
 *  POST /api/sessions/:id/regenerate -> SessionRecord (re-runs the debrief; status 'debriefing').
 *  DELETE /api/sessions/:id -> { ok: true } */

export type ProgressReportStatus = 'pending' | 'done' | 'failed';
export interface ProgressReportRecord {
  id: string;
  createdAt: number;
  status: ProgressReportStatus;
  markdown: string | null;
  sessionIds: string[];
  error: string | null;
}
/** POST /api/progress/reports { sessionIds?: string[] } (default: every 'done' session, max 20) -> ProgressReportRecord (status 'pending')
 *  GET /api/progress/reports -> { reports: ProgressReportRecord[] } (newest first)
 *  GET /api/progress/reports/:id -> ProgressReportRecord (poll until not 'pending') */
export interface ProgressReportListResponse {
  reports: ProgressReportRecord[];
}
